import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

const EMP_ID_QUERY = 'empId'

/**
 * Employee-scoped HR sub-lists: URL carries `?empId=`; opening without it shows the picker dialog.
 */
export function useEmployeeListScope() {
  const [searchParams, setSearchParams] = useSearchParams()

  const empId = useMemo(() => {
    const raw = searchParams.get(EMP_ID_QUERY) ?? searchParams.get('EmpId')
    const n = raw ? Number(raw) : 0
    return n > 0 && !Number.isNaN(n) ? n : 0
  }, [searchParams])

  const [pickerOpen, setPickerOpen] = useState(false)

  useEffect(() => {
    setPickerOpen(empId === 0)
  }, [empId])

  const confirmEmployee = useCallback(
    (id: number) => {
      if (!id || Number.isNaN(id)) return
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set(EMP_ID_QUERY, String(id))
          return next
        },
        { replace: true }
      )
      setPickerOpen(false)
    },
    [setSearchParams]
  )

  const openEmployeePicker = useCallback(() => {
    setPickerOpen(true)
  }, [])

  const handlePickerOpenChange = useCallback((open: boolean) => {
    if (!open && empId === 0) return
    setPickerOpen(open)
  }, [empId])

  return {
    empId,
    pickerOpen,
    confirmEmployee,
    openEmployeePicker,
    handlePickerOpenChange,
  }
}
